import { ORDERS_PRO_ONLY } from "./plans.js";

// Dirección pública del menú de cada negocio. La usan el panel, el QR y el cartel.
export const MENU_BASE_URL = "https://miqrapp.com";

export const menuUrl = (usuarioEmpresa) => `${MENU_BASE_URL}/${usuarioEmpresa}`;

// La que abre el QR: con ?o=qr las estadísticas distinguen al que escaneó del que entró por el link.
export const qrMenuUrl = (usuarioEmpresa) => `${menuUrl(usuarioEmpresa)}?o=qr`;

// Rutas de la app que no pueden usarse como nombre de usuario del negocio.
export const RESERVED_USERNAMES = ["login", "register", "cuenta", "suscripcion", "not-found", "preguntas-frecuentes", "terminos-y-condiciones", "politicas-de-privacidad", "arrepentimiento", "assets", "static"];

// Lo que ve cualquiera en el menú público. Se copia del doc privado users/{email} a
// menus/{usuarioEmpresa}, que es el único que se puede leer sin sesión. Tiene que
// coincidir con la lista de firestore.rules.
export const PUBLIC_FIELDS = [
    "id", "usuarioEmpresa", "nombreEmpresa", "descripcionEmpresa", "logo", "portada", "promos",
    "calle", "altura", "ciudad", "provincia", "telefono", "horario", "horarioSec",
    "categories", "subscribed", "pedidos", "costoEnvio", "resenasUrl", "fichaGoogle", "mpConectado",
    "bodyColor", "selectColor", "fontColor", "arrowColor", "buttonColor"
];

// Número para wa.me: solo dígitos y con código de país. Si el dueño cargó un número
// argentino sin el 54 (o sin el 9 de celular), se lo agrega.
export const whatsappNumber = (telefono) => {
    const digits = String(telefono || "").replace(/\D/g, "").replace(/^0+/, "");
    if (digits.length === 10) return "549" + digits;
    if (digits.length === 12 && digits.startsWith("54")) return "549" + digits.slice(2);
    return digits;
}

// Si el plan del negocio incluye los pedidos por WhatsApp (ORDERS_PRO_ONLY en plans.js).
export const ordersAvailable = (business) => !ORDERS_PRO_ONLY || business?.subscribed === true;

// El carrito del menú aparece si el dueño lo activó en el panel (`pedidos`),
// tiene WhatsApp cargado y su plan lo incluye.
export const takesOrders = (business) => business?.pedidos === true && !!whatsappNumber(business.telefono) && ordersAvailable(business);

// Etiquetas de cada producto (campo `etiquetas` de products): las sugeridas o las que
// escriba el negocio Pro, hasta MAX_TAGS por producto y TAG_MAX_LENGTH letras cada una.
export const PRODUCT_TAGS = ["Nuevo", "Recomendado", "Promo", "Vegano", "Sin TACC", "Picante"];
export const MAX_TAGS = 4;
export const TAG_MAX_LENGTH = 20;

export const cleanTag = (value) => String(value || "").replace(/[<>]/g, "").replace(/\s+/g, " ").trim().slice(0, TAG_MAX_LENGTH);

// Agotados y etiquetas son del plan Pro: en el menú de un negocio Gratis no se muestran las etiquetas.
export const productTags = (business, product) => business?.subscribed === true && Array.isArray(product.etiquetas)
    ? product.etiquetas.map(cleanTag).filter(Boolean).slice(0, MAX_TAGS)
    : [];

export const publicMenu = (business) => Object.fromEntries(
    PUBLIC_FIELDS.filter((key) => business[key] !== undefined).map((key) => [key, business[key]])
);

export const cleanUsername = (value) => value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

export const formatPrice = (price) => {
    const n = Number(price);
    return Number.isFinite(n) ? "$" + n.toLocaleString("es-AR") : "$" + price;
}

// Igual que storage.rules: imágenes de hasta 5 MB.
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// ---- Reseñas de Google ----
// `resenasUrl`: el link para dejar reseña que da el Perfil de Empresa de Google (g.page/r/...,
// search.google.com/local/writereview?..., maps.app.goo.gl/...). Mismo patrón que firestore.rules.
export const REVIEW_URL_MAX = 300;
export const REVIEW_URL_PATTERN = /^https:\/\/(g\.page|g\.co|maps\.app\.goo\.gl|goo\.gl|(www\.|maps\.|search\.)?google\.com(\.ar)?)\/[^<>"' ]*$/;

// `fichaGoogle`: muestra en el menú la ficha del local en Google Maps (estrellas y cantidad
// de reseñas). Usa la Maps Embed API, que es gratis pero pide una API key del proyecto de
// Google Cloud de MiQRapp en REACT_APP_GOOGLE_MAPS_KEY. Sin key no se muestra.
export const MAPS_EMBED_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

// Lo que se busca en Maps: nombre y dirección, así cae en la ficha del negocio.
export const placeQuery = (b) => [b.nombreEmpresa, [b.calle, b.altura].filter(Boolean).join(" "), b.ciudad, b.provincia]
    .map((part) => String(part || "").trim()).filter(Boolean).join(", ");

export const mapsEmbedUrl = (b) => MAPS_EMBED_KEY && placeQuery(b)
    ? "https://www.google.com/maps/embed/v1/place?language=es&key=" + encodeURIComponent(MAPS_EMBED_KEY) + "&q=" + encodeURIComponent(placeQuery(b))
    : "";

export const reviewLink = (b) => typeof b?.resenasUrl === "string" && REVIEW_URL_PATTERN.test(b.resenasUrl) ? b.resenasUrl : "";

// Si el menú tiene algo de reseñas para mostrar (para todos los planes).
export const showsReviews = (b) => !!reviewLink(b) || (b?.fichaGoogle === true && !!mapsEmbedUrl(b));
